import React, { useEffect } from 'react'
import { createPortal } from 'react-dom'
import { WebViewerInstance } from '@pdftron/webviewer'
import { Coordinates, Marker } from '../../type'
import { MarkerComponent } from './Marker.component'
import { MarkerPlacement } from './MarkerPlacement.component'

interface MarkerPortalProps {
  instanceRef: React.MutableRefObject<WebViewerInstance | null>
  markerContainer: HTMLElement | null
  pageNumber: number
  canEdit: boolean
  marker?: Marker
  markerStatus: 'visible' | 'add_selecting_position' | 'add_dragging_position'
  dragState: { isDragging: boolean }
  eventHandlers: {
    handleMove: (event: MouseEvent | TouchEvent) => void
    handleEnd: (event: MouseEvent | TouchEvent) => void
    handleMarkerMouseDown: () => void
    addMarker: (coordinates: Coordinates) => void
    deleteMarker: () => void
  }
}

export const MarkerPortal: React.FC<MarkerPortalProps> = ({
  markerContainer,
  instanceRef,
  pageNumber,
  canEdit,
  marker,
  markerStatus,
  dragState,
  eventHandlers,
}) => {
  useEffect(() => {
    if (!dragState.isDragging || !markerContainer || !canEdit) return

    const handleTouchMove = (e: TouchEvent) => {
      if (e.touches.length > 1) return
      eventHandlers.handleMove(e)
    }

    markerContainer.addEventListener('touchmove', handleTouchMove, { passive: true })
    markerContainer.addEventListener('touchend', eventHandlers.handleEnd)
    markerContainer.addEventListener('touchcancel', eventHandlers.handleEnd)
    markerContainer.addEventListener('mousemove', eventHandlers.handleMove, { passive: true })
    markerContainer.addEventListener('mouseup', eventHandlers.handleEnd)

    return () => {
      markerContainer.removeEventListener('touchmove', handleTouchMove)
      markerContainer.removeEventListener('touchend', eventHandlers.handleEnd)
      markerContainer.removeEventListener('touchcancel', eventHandlers.handleEnd)
      markerContainer.removeEventListener('mousemove', eventHandlers.handleMove)
      markerContainer.removeEventListener('mouseup', eventHandlers.handleEnd)
    }
  }, [dragState.isDragging, markerContainer, canEdit, eventHandlers])

  useEffect(() => {
    if (!markerContainer) return

    if (markerStatus === 'add_selecting_position' || dragState.isDragging) {
      markerContainer.style.cursor = 'none'
    }

    return () => {
      if (markerStatus === 'add_selecting_position' || dragState.isDragging) {
        markerContainer.style.cursor = ''
      }
    }
  }, [markerContainer, markerStatus, dragState.isDragging])

  const handleAdd = (coordinates: Coordinates) => {
    eventHandlers.addMarker(coordinates)
  }

  if (!markerContainer) return null

  return createPortal(
    <>
      {markerStatus === 'add_selecting_position' && (
        <MarkerPlacement
          canEdit={canEdit}
          markerContainer={markerContainer}
          containerRef={instanceRef}
          pageNumber={pageNumber}
          onAdd={handleAdd}
        />
      )}

      {marker && marker.pageNumber === pageNumber && (
        <MarkerComponent
          marker={marker}
          canEdit={canEdit}
          isSelected={false}
          isDragging={dragState.isDragging}
          onMouseDown={eventHandlers.handleMarkerMouseDown}
          onDelete={eventHandlers.deleteMarker}
          showDelete
        />
      )}
    </>,
    markerContainer,
  )
}
